'use client';

import { useParams } from 'next/navigation';
import { BsTabletLandscape } from 'react-icons/bs';
import { CiMobile1 } from 'react-icons/ci';
import { FiMonitor } from 'react-icons/fi';
import { IoIosArrowDown } from 'react-icons/io';
import { IoAdd, IoClose } from 'react-icons/io5';
import { TbShoppingCart } from 'react-icons/tb';

import cn from '@/lib/clsxm';

import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import TabGroup, { Tab, Tabs } from '@/components/@base/tab';
import { Text } from '@/components/@base/typography';
import XImage from '@/components/@base/x-image';
import { useGetProductDemo } from '@/components/app/demo/services/hooks';
import AddToCartButton from '@/components/shared/components/add-to-cart-button';
import ZhkLogo from '@/components/shared/components/logo';
import { LOGO_SIZE } from '@/components/shared/components/logo/type';
import PriceView from '@/components/shared/components/price-view';
import SmartOffer from '@/components/shared/components/smart-offers';

import { DEMO_VIEW_MODE, useProductDemoStore } from '@/store/product-demo.store';

import { DATA_CY_PRODUCT_TITLE } from '@/constant/cypress';
import ICON_SIZE, { ICON_COLOR } from '@/constant/icon-size-color';
import imagesList from '@/constant/images-list';
import localization from '@/constant/localization';

export default function Header() {
  const { slug } = useParams<{ slug: string }>();
  const { data } = useGetProductDemo(slug);
  const { setViewMode, setShowOthers, showOthers, isHeaderVisible, setIsHeaderVisible } =
    useProductDemoStore();
  const finalDiscount = data?.isOnSale ? data?.discount : 0;

  if (!data) return null;

  return (
    <>
      {isHeaderVisible && (
        <Container
          center
          className='sticky z-40 h-[74px] w-full justify-between bg-white p-[15px] shadow-[0px_20px_60px_-15px_rgba(0,0,0,0.15)] transition-all duration-500 md:h-[100px]'
        >
          <Container center className='w-full justify-start'>
            <ZhkLogo size={LOGO_SIZE.LG} darkMode />
            <Container center className='hidden gap-[10px] pr-[50px] pl-[20px] md:flex'>
              <XImage
                width={28}
                height={28}
                className='rounded-full'
                src={
                  data.store?.logo?.publicLink
                    ? data.store?.logo?.publicLink
                    : imagesList.images.productDefaultCover
                }
                alt={data.store?.logo?.title}
              />
              <Text className='text-[13px] text-[#76767C]'>{data.store?.name}</Text>
            </Container>
            <Container
              center
              className='hidden h-[50px] max-w-[340px] justify-between gap-4 rounded-[4px] border-[1px] border-[#EEF0F4] p-[5px] md:flex'
            >
              <Text
                data-cy={DATA_CY_PRODUCT_TITLE}
                className={cn(
                  'max-w-[200px] truncate p-[10px]',
                  !data.demos?.length && 'w-full max-w-fit!',
                )}
              >
                {data.title}
              </Text>
              {data.demos?.length > 0 && (
                <Button
                  className='h-10 w-[98px] flex-row-reverse gap-0 bg-[#F0F8FF] px-[15px] py-[10px] text-[12px] font-bold text-[#6097F3]'
                  onClick={() => setShowOthers(!showOthers)}
                  Icon={
                    <IoIosArrowDown
                      color={ICON_COLOR.primary}
                      size={ICON_SIZE.sm}
                      className='ui-open:rotate-180 transform transition ease-in'
                    />
                  }
                >
                  {localization.otherDemos}
                </Button>
              )}
            </Container>
          </Container>
          <Container className='hidden w-full justify-center md:flex'>
            <TabGroup
              onChange={(index) => {
                if (index === 0) setViewMode(DEMO_VIEW_MODE.DESKTOP);
                else if (index === 1) setViewMode(DEMO_VIEW_MODE.TABLET);
                else setViewMode(DEMO_VIEW_MODE.MOBILE);
              }}
            >
              <Tabs className='flex border-[#EEF0F4]'>
                <Tab className='ui-selected:border-[#FF9606] flex h-[50px] w-[50px] items-center justify-center rounded-r-[4px] border-[1px] border-[#E5E8EB]'>
                  <FiMonitor
                    className='ui-selected:text-[#FF9606] text-[#878F9B]'
                    size={ICON_SIZE.md}
                  />
                </Tab>
                <Tab className='ui-selected:border-[#FF9606] flex h-[50px] w-[50px] items-center justify-center border-[1px] border-x-[1px] border-[#E5E8EB] border-x-[#E5E8EB]'>
                  <BsTabletLandscape
                    className='ui-selected:text-[#FF9606] text-[#878F9B]'
                    size={ICON_SIZE.md}
                  />
                </Tab>
                <Tab className='ui-selected:border-[#FF9606] flex h-[50px] w-[50px] items-center justify-center rounded-l-[4px] border-[1px] border-[#E5E8EB]'>
                  <CiMobile1
                    className='ui-selected:text-[#FF9606] text-[#878F9B]'
                    size={ICON_SIZE.lg}
                  />
                </Tab>
              </Tabs>
            </TabGroup>
          </Container>
          <Container center className='w-full justify-end gap-[10px]'>
            <PriceView
              sellable={data.sellable}
              price={data.price}
              lastPrice={data.lastPrice}
              showDiscountLabel
              saleEndedAt={data.saleEndedAt}
              discount={finalDiscount}
              priceClassName='text-[22px] text-[#424244]'
              priceWrapperClassName='pl-2 items-center gap-2'
              className='hidden items-center gap-2 md:flex'
              lastPriceClassName='text-[18px]'
            />
            <AddToCartButton
              Icon={<TbShoppingCart color={ICON_COLOR.white} size={ICON_SIZE.sm} />}
              className='h-[44px] w-[154px]'
              productId={data.id}
              slug={data.slug}
              isFree={data.price === 0}
            >
              <Text className='text-[13px] font-bold'>{localization.addToCart}</Text>
            </AddToCartButton>
            <Button
              center
              className='h-[44px] w-[44px] rounded-md bg-[#F9FAFC]'
              onClick={() => setIsHeaderVisible(false)}
            >
              <IoClose color={ICON_COLOR.gray} size={ICON_SIZE.md} />
            </Button>
          </Container>
          <SmartOffer />
        </Container>
      )}
      {!isHeaderVisible && (
        <Button
          center
          className='fixed top-4 left-8 h-[44px] w-[44px] rounded-md bg-[#fea000] transition-all duration-500'
          onClick={() => setIsHeaderVisible(true)}
        >
          <IoAdd color={ICON_COLOR.white} size={ICON_SIZE.lg} />
        </Button>
      )}
    </>
  );
}
